import React, { useState } from "react";
import { MapPin, Phone, Mail, Clock, Send, Loader2, Navigation, ExternalLink } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { useToast } from "../../hooks/use-toast";
import { company } from "../../mock";

const fullAddress = `${company.legalName}, ${company.address.line1}, ${company.address.line2}, ${company.address.state}`;
const mapsQuery = encodeURIComponent(fullAddress);
const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${mapsQuery}`;
const mapEmbedUrl = `https://www.google.com/maps?q=${mapsQuery}&output=embed`;
const telLink = `tel:${company.phone.replace(/\s/g, "")}`;
const mailLink = `mailto:${company.email}`;

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Missing fields", description: "Please fill in name, email and message." });
      return;
    }
    setLoading(true);
    setTimeout(() => {
      const stored = JSON.parse(localStorage.getItem("manishlabchem_contacts") || "[]");
      stored.push({ ...form, id: Date.now() });
      localStorage.setItem("manishlabchem_contacts", JSON.stringify(stored));
      setLoading(false);
      setForm({ name: "", email: "", phone: "", message: "" });
      toast({ title: "Message sent", description: "We will be in touch shortly." });
    }, 800);
  };

  return (
    <section id="contact" className="py-20 lg:py-28 bg-slate-50">
      <div className="max-w-7xl mx-auto px-5 lg:px-6">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold text-teal-700">Get in touch</div>
            <h2 className="mt-3 font-display font-extrabold text-slate-900 text-[34px] lg:text-[44px] leading-[1.08]">
              Let's discuss your <span className="text-gradient-anim">requirement</span>
            </h2>
            <p className="mt-4 text-slate-600 text-[15.5px] leading-relaxed">
              Speak with a sales engineer about pricing, lead time, contract supply or technical specifications. Click any contact below to call, email, or navigate.
            </p>

            <div className="mt-7 space-y-3">
              <InfoCard
                icon={MapPin}
                title="Manufacturing & Head Office"
                primary={[company.address.line1, company.address.line2, company.address.state]}
                actions={[
                  { label: "Get directions", icon: Navigation, href: directionsUrl, external: true, primary: true },
                  { label: "View on Map", icon: ExternalLink, href: `https://www.google.com/maps?q=${mapsQuery}`, external: true }
                ]}
              />
              <InfoCard
                icon={Phone}
                title="Call us"
                primary={[company.phone]}
                href={telLink}
                actions={[
                  { label: `Call ${company.phone}`, icon: Phone, href: telLink, primary: true },
                  { label: "WhatsApp", icon: Send, href: `https://wa.me/${company.whatsapp}?text=${encodeURIComponent("Hi Manish Labchem, I want to enquire about your products.")}`, external: true }
                ]}
              />
              <InfoCard
                icon={Mail}
                title="Email us"
                primary={[company.email]}
                href={mailLink}
                actions={[
                  { label: "Send email", icon: Mail, href: mailLink, primary: true }
                ]}
              />
              <InfoCard
                icon={Clock}
                title="Working hours"
                primary={[company.hours, "Sunday: Closed"]}
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 p-6 lg:p-8 shadow-sm">
              <h3 className="font-display font-bold text-slate-900 text-[22px]">Send us a message</h3>
              <p className="text-[14px] text-slate-500 mt-1">We typically respond within 4 business hours.</p>

              <div className="mt-5 grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="c-name">Name *</Label>
                  <Input id="c-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="c-email">Email *</Label>
                  <Input id="c-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@company.com" />
                </div>
                <div className="sm:col-span-2 space-y-1.5">
                  <Label htmlFor="c-phone">Phone</Label>
                  <Input id="c-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 9XXXXXXXXX" />
                </div>
                <div className="sm:col-span-2 space-y-1.5">
                  <Label htmlFor="c-msg">Message *</Label>
                  <Textarea id="c-msg" rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="How can we help?" />
                </div>
              </div>
              <div className="mt-5 flex items-center justify-between">
                <p className="text-[12.5px] text-slate-500">Your details stay confidential.</p>
                <Button type="submit" disabled={loading} className="bg-teal-700 hover:bg-teal-800 text-white h-11 px-6 font-semibold">
                  {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending</> : <><Send className="h-4 w-4 mr-2" /> Send message</>}
                </Button>
              </div>
            </form>

            <div className="mt-5 bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg brand-band flex items-center justify-center shrink-0">
                  <Navigation className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="font-display font-bold text-slate-900 text-[15px]">Prefer to drop by?</div>
                  <p className="text-[13.5px] text-slate-600 mt-1 leading-relaxed">
                    We welcome plant visits by appointment. Reach out and our team will arrange a site walkthrough of our Vapi GIDC facility &mdash; including the QC lab, production floor and dispatch yard.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Full-width interactive map */}
        <div className="mt-12">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
            <div>
              <div className="text-[12px] uppercase tracking-[0.22em] font-semibold text-teal-700">Find us</div>
              <h3 className="mt-2 font-display font-extrabold text-slate-900 text-[22px]">
                Vapi GIDC Phase 2 &mdash; Manish Labchem Plant
              </h3>
            </div>
            <a
              href={directionsUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 self-start sm:self-auto px-4 h-10 rounded-md bg-teal-700 hover:bg-teal-800 text-white text-[13.5px] font-semibold transition-colors"
            >
              <Navigation className="h-4 w-4" /> Open in Google Maps
            </a>
          </div>
          <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-md">
            <iframe
              title="Manish Labchem location map"
              src={mapEmbedUrl}
              width="100%"
              height="380"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const InfoCard = ({ icon: Icon, title, primary, actions = [], href }) => {
  const Wrap = href ? "a" : "div";
  const wrapProps = href ? { href, className: "block" } : {};
  return (
    <Wrap {...wrapProps} className={`block group ${href ? "cursor-pointer" : ""}`}>
      <div className={`info-card-row flex items-start gap-4 p-4 rounded-xl bg-white border border-slate-200 ${href ? "group-hover:border-teal-400 group-hover:shadow-md group-hover:-translate-y-0.5" : ""}`}>
        <div className="h-10 w-10 rounded-lg bg-teal-50 border border-teal-100 flex items-center justify-center shrink-0 group-hover:bg-teal-700 group-hover:border-teal-700 group-hover:scale-105 group-hover:rotate-[-4deg] transition-all duration-300 ease-out">
          <Icon className="h-5 w-5 text-teal-700 group-hover:text-white transition-colors duration-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-slate-900 text-[14.5px]">{title}</div>
          <div className="text-[13.5px] text-slate-600 mt-0.5 leading-relaxed">
            {primary.map((l, i) => (<div key={i}>{l}</div>))}
          </div>
          {actions.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {actions.map((a, i) => {
                const AIcon = a.icon;
                const base = "action-btn group/btn inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12.5px] font-semibold relative overflow-hidden";
                const style = a.primary
                  ? "action-btn-primary text-white"
                  : "action-btn-ghost text-slate-700 border border-slate-200";
                return (
                  <a
                    key={i}
                    href={a.href}
                    target={a.external ? "_blank" : undefined}
                    rel={a.external ? "noreferrer" : undefined}
                    onClick={(e) => e.stopPropagation()}
                    className={`${base} ${style}`}
                  >
                    <AIcon className="h-3.5 w-3.5 action-btn-icon" />
                    <span className="action-btn-label">{a.label}</span>
                    <span className="action-btn-sheen" aria-hidden="true" />
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Wrap>
  );
};

export default Contact;
